import { NavigationContainerRef } from '@react-navigation/core';
import * as Sentry from '@sentry/react';
import formatISO from 'date-fns/formatISO';
import { RefObject, useCallback, useRef } from 'react';
import { Platform } from 'react-native';
import { v4 } from 'uuid';
import { OnboardingStateManager } from '../../flows/user/functions/onboardingState';
import { makeDebug } from '../../utils/makeDebug';
import { onErrorIgnore } from '../../utils/onErrorIgnore';
import { safeMarkDevFlag } from '../../utils/safeMarkDevFlag';
import { useCurrentUserId } from '../auth/utils';
import { config } from '../config';
import { isDebugEnabled } from '../debugging';
import { trackNavigatorPerformance } from '../debugging/crashTracking';
import { utmParameters$ } from '../dynamic-links/campaignTracker';
import { Timestamp } from '../firebase/firebaseAccess';
import { usePersistFn } from '../hooks/usePersistFn';
import { useDeviceId, useSessionId } from '../user/sessionTracker';
import { getDeepLinkUrlFromState, linking } from './linking/config';
import { nameOfCurrentRoute } from './utils/nameOfCurrentRoute';

const debug = makeDebug('navigation:tracking', isDebugEnabled('navigation'));

const kDisableCsPageViewAttribution = safeMarkDevFlag(false);

export function sensitizeParams(params?: any, level = 1): any {
  if (typeof params === 'function') {
    return undefined;
  }
  if (typeof params === 'number' && (isNaN(params) || !isFinite(params))) {
    return undefined;
  }
  if (
    params == null ||
    typeof params === 'string' ||
    typeof params === 'number' ||
    typeof params === 'boolean'
  ) {
    return params;
  }
  if (params instanceof Timestamp) {
    return formatISO(params.toDate());
  }
  if (level === 0) {
    return undefined;
  }
  if (Array.isArray(params)) {
    return params.map(p => sensitizeParams(p, level - 1));
  }
  if (typeof params === 'object') {
    const result = {} as any;
    for (const key of Object.keys(params)) {
      const sensitizedParams = sensitizeParams(params[key], level - 1);
      if (typeof sensitizedParams === 'object') {
        continue;
      }
      result[key] = sensitizedParams;
    }
    return result;
  }
  return undefined;
}

const useTrackCsPageViewEvent = () => {
  const uid = useCurrentUserId();
  const deviceId = useDeviceId();
  const sessionId = useSessionId();

  return usePersistFn((routeName: string, path: string) => {
    if (
      kDisableCsPageViewAttribution ||
      routeName === 'sellerProfile' ||
      routeName === 'userProfile' ||
      routeName === 'profile'
    ) {
      return;
    }

    const [pathname, queryString] = path.split('?');
    if (queryString) {
      if (queryString.includes('%5B') && queryString.includes('%5D')) {
        path = pathname;
      }
    }

    const utmParameters = utmParameters$.value ?? {};

    const data = {
      utm_source: utmParameters['utm_source'] || 'internal',
      utm_medium: utmParameters['utm_medium'] || 'organic',
      utm_campaign: utmParameters['utm_campaign'] || v4(),
      utm_term: utmParameters['utm_term'] || 'organic',
      utm_content: 'navigation',
      event_timestamp: Date.now(),
      customer_id: 'popshoplive',
      session_id: deviceId,
      context: {
        event_type: 'PAGE_VIEW',
        session_id: deviceId,
        psl_user_id: uid,
        psl_session_id: sessionId,
        route: '/' + routeName,
        url:
          Platform.OS === 'web'
            ? `${config.url}${path}`
            : `${config.scheme}://${path.slice(1) || 'home'}`,
        utm_adgroup: utmParameters['utm_adgroup'] || '',
        utm_campaignid: utmParameters['utm_campaignid'],
        utm_adgroupid: utmParameters['utm_adgroupid'],
        utm_network: utmParameters['utm_network'],
        utm_matchtype: utmParameters['utm_matchtype'],
        utm_creative: utmParameters['utm_creative'],
        utm_device: utmParameters['utm_device'],
        utm_category: 'popshoplive',
        utm_referral_name: utmParameters['utm_referral_name'] || '',
        utm_referral_medium: utmParameters['utm_referral_medium'] || '',
        utm_referral_code: utmParameters['utm_referral_code'] || '',
      },
    };
    debug('tracking cs page view: %O', data);
    fetch(config.csEventTrackingEndpoint, {
      method: 'PUT',
      headers: {
        'content-type': 'application/json; charset=UTF-8',
      },
      body: JSON.stringify(data),
    }).catch(onErrorIgnore);
  });
};

// https://reactnavigation.org/docs/screen-tracking/#example
export const useTrackNavigator = (
  prefix: string,
  passedInRef?: RefObject<NavigationContainerRef<any>>,
) => {
  const newRef = useRef<NavigationContainerRef<any>>(null);
  const ref = passedInRef ?? newRef;
  const name = useRef<string>(prefix);
  const initialRoute = useRef<any>();
  const trackCsPageViewEvent = useTrackCsPageViewEvent();

  const onReady = useCallback(() => {
    if (ref.current == null) {
      return;
    }

    trackNavigatorPerformance(ref.current);
    const state = ref.current.getState();
    const routeName = nameOfCurrentRoute(prefix, state);
    const currentRoute = ref.current.getCurrentRoute();

    if (currentRoute == null) {
      return;
    }

    if (!initialRoute.current) {
      initialRoute.current = currentRoute;
      OnboardingStateManager.shared.appLaunchedWithInitialRoute(currentRoute);
    }

    const params = sensitizeParams(currentRoute.params);
    const path =
      state != null
        ? getDeepLinkUrlFromState(state, linking.config, false)
        : '/';
    trackCsPageViewEvent(
      routeName,
      `${prefix ? '/' + prefix.replace(/\/+$/, '') : ''}${path}`,
    );

    // analytics.screen(routeName, params);
    trackScreenChange({
      from: name.current ?? 'Launch',
      to: routeName,
      params,
    });
    name.current = routeName;
  }, [prefix, ref, trackCsPageViewEvent]);

  const onStateChange = useCallback(() => {
    if (!ref.current) {
      return;
    }

    const previousRouteName = name.current;
    const currentRoute = ref.current.getCurrentRoute();
    const state = ref.current.getState();
    const routeName = nameOfCurrentRoute(prefix, state);
    const params =
      currentRoute != null ? sensitizeParams(currentRoute.params) : {};

    if (currentRoute == null || routeName === previousRouteName) {
      return;
    }

    const path =
      state != null
        ? getDeepLinkUrlFromState(state, linking.config, false)
        : '/';

    // debug(
    //   'navigation state route changed to %s (path: %s), params: %O',
    //   routeName,
    //   path,
    //   params,
    // );

    trackCsPageViewEvent(
      routeName,
      `${prefix ? '/' + prefix.replace(/\/+$/, '') : ''}${path}`,
    );

    // analytics.screen(routeName, params);

    if (!initialRoute.current) {
      initialRoute.current = currentRoute;
      OnboardingStateManager.shared.appLaunchedWithInitialRoute(currentRoute);
    }

    trackScreenChange({
      from: name.current ?? 'Launch',
      to: routeName,
      params,
    });

    name.current = routeName;
  }, [prefix, ref, trackCsPageViewEvent]);

  return {
    ref,
    onReady,
    onStateChange,
  };
};

export const trackScreenChange = ({
  params,
  from,
  to,
}: {
  from: string;
  to: string;
  params: any;
}) => {
  Sentry.addBreadcrumb({
    type: 'navigation',
    category: 'navigation',
    data: {
      from,
      to,
      params: JSON.stringify(params, null, 2),
    },
  });
};
